import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Reusable Copy-to-Clipboard button component
 * Shows "Copied!" feedback for 1.5 seconds after successful copy
 */
const CopyButton = ({ 
  text, 
  label = "Copy", 
  variant = "ghost",
  size = "sm",
  className = "",
  showLabel = true,
  onCopy
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) {
      toast.error("Nothing to copy");
      return;
    }

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);
        
        if (!successful) {
          throw new Error("execCommand failed");
        }
      }

      setCopied(true);
      toast.success("Copied to clipboard!");
      
      // Reset after 1.5 seconds
      setTimeout(() => setCopied(false), 1500);
      
      // Call optional callback
      if (onCopy) onCopy();
      
    } catch (err) {
      console.error("Copy failed:", err);
      toast.error("Copy failed — please select text manually");
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleCopy}
      className={`transition-all ${copied ? "text-green-600" : "text-[#6D6F7C] hover:text-[#3D405B]"} ${className}`}
      data-testid="copy-button"
    >
      {copied ? (
        <>
          <Check className="w-4 h-4" />
          {showLabel && <span className="ml-1">Copied!</span>}
        </>
      ) : (
        <>
          <Copy className="w-4 h-4" />
          {showLabel && <span className="ml-1">{label}</span>}
        </>
      )}
    </Button>
  );
};

export default CopyButton;
